import { Link, useParams } from "react-router-dom";
import { ArrowRight, ArrowUpRight, MapPin } from "lucide-react";
import * as Icons from "lucide-react";
import Seo from "@/components/Seo";
import Faq from "@/components/Faq";
import { MaskLines, Reveal, SectionHead } from "@/components/Reveal";
import { CITIES, SERVICES, getService, serviceImage, PHONE, PHONE_HREF, EMAIL_HREF } from "@/data";
import { getCyService, CY_SERVICES, CY_SERVICE_UI as U } from "@/data/cy-services";
import NotFound from "./NotFound";

const WelshServicePage = () => {
  const { slug } = useParams();
  const service = getService(slug);
  const cy = getCyService(slug);
  if (!service || !cy) return <NotFound />;
  const idx = SERVICES.indexOf(service);
  const related = SERVICES.filter((s) => s.slug !== slug).slice(idx % 5, (idx % 5) + 3);
  const Icon = Icons[service.icon] || Icons.Shield;

  return (
    <div data-testid={`service-cy-page-${service.slug}`}>
      <Seo
        title={`${cy.nameCy} Cymru | Trwyddedig SIA | Xtreme Protection`}
        description={`${cy.shortCy} Mae Xtreme Protection yn darparu ${cy.nameCy.toLowerCase()} ar draws 35+ o drefi a dinasoedd Cymru gyda swyddogion trwyddedig SIA. 24/7 — ffoniwch +44 2046 402840.`}
        lang="cy"
        path={`/cy/gwasanaethau/${service.slug}`}
        altEn={`/services/${service.slug}`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: cy.nameCy,
          provider: { "@type": "LocalBusiness", name: "Xtreme Protection — Wales Security Services", telephone: "+44 2046 402840", email: "admin@xtremeprotection.uk" },
          areaServed: { "@type": "AdministrativeArea", name: "Cymru" },
        }}
      />

      <section className="xp-noise relative overflow-hidden border-b border-[#1C2027] pb-16 pt-32">
        <div className="absolute inset-0">
          <img src={serviceImage(idx)} alt={`${cy.nameCy} yng Nghymru — Xtreme Protection`} className="h-full w-full object-cover opacity-25" loading="lazy" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0C10] via-[#0A0C10]/80 to-[#0A0C10]/40" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6">
          <p className="eyebrow flex items-center gap-3">
            <span className="h-px w-10 bg-[#F5A800]" />
            {U.eyebrowPrefix} {String(idx + 1).padStart(2, "0")} o 17 // {U.eyebrowSuffix}
          </p>
          <div className="mt-6 flex items-center gap-4">
            <span className="flex h-14 w-14 shrink-0 items-center justify-center border border-[#F5A800]/40 bg-[#F5A800]/10">
              <Icon className="h-7 w-7 text-[#F5A800]" />
            </span>
            <MaskLines className="font-condensed text-5xl font-bold uppercase leading-[0.9] tracking-tight text-white sm:text-6xl lg:text-7xl" lines={[cy.nameCy, U.h1Suffix]} />
          </div>
          <Reveal delay={0.35}>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-[#C8CCD2] sm:text-lg">{cy.shortCy}</p>
          </Reveal>
          <Reveal delay={0.45}>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href={PHONE_HREF} data-testid="service-cy-call-btn" className="group flex items-center gap-2 bg-[#F5A800] px-6 py-3.5 text-sm font-bold uppercase tracking-[0.14em] text-[#0A0C10] transition-colors duration-300 hover:bg-white">
                {U.ctaCall} {PHONE}
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </a>
              <Link to="/cy/lleoliadau" data-testid="service-cy-locations-btn" className="flex items-center gap-2 border border-white/25 px-6 py-3.5 text-sm font-bold uppercase tracking-[0.14em] text-white transition-colors duration-300 hover:border-[#F5A800] hover:text-[#F5A800]">
                {U.ctaLocations}
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:py-20">
        <SectionHead index={U.intelIndex} eyebrow={U.intelEyebrow} title={`${cy.nameCy} ${U.intelTitleSuffix}`} testid="service-cy-intro-heading" />
        <div className="mt-8 grid gap-10 lg:grid-cols-2">
          <Reveal>
            <p className="text-base leading-relaxed text-[#9BA1AB]">{cy.intro}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h3 className="font-condensed text-xl font-bold uppercase tracking-tight text-white">{U.deliveryTitle}</h3>
            <p className="mt-3 text-base leading-relaxed text-[#9BA1AB]">{cy.delivery}</p>
            <h3 className="font-condensed mt-8 text-xl font-bold uppercase tracking-tight text-white">{U.standardsTitle}</h3>
            <p className="mt-3 text-base leading-relaxed text-[#9BA1AB]">{cy.standards}</p>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-[#1C2027] bg-[#0D1015] py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <SectionHead index={U.coverageIndex} eyebrow={U.coverageEyebrow} title={`${cy.nameCy} ${U.coverageTitleSuffix}`} testid="service-cy-coverage-heading" />
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-[#9BA1AB]">{U.coverageSub}</p>
          <div className="mt-10 grid gap-px border border-[#1C2027] bg-[#1C2027] sm:grid-cols-2 lg:grid-cols-4" data-testid="service-cy-cities-grid">
            {CITIES.map((c, i) => (
              <Reveal key={c.slug} delay={(i % 4) * 0.05} y={12} className="h-full">
                <Link to={`/cy/lleoliadau/${c.slug}`} data-testid={`service-cy-city-${c.slug}`} className="group flex h-full items-center justify-between bg-[#10131A] p-4 transition-colors duration-300 hover:bg-[#161A22]">
                  <div>
                    <p className="font-condensed text-base font-bold uppercase tracking-wide text-white transition-colors duration-300 group-hover:text-[#F5A800]">{c.name}</p>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-white/40">{c.county}</p>
                  </div>
                  <MapPin className="h-3.5 w-3.5 shrink-0 text-[#F5A800]/60 transition-transform duration-300 group-hover:scale-125" />
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-4 py-16 sm:px-6 lg:py-20">
        <SectionHead index={U.faqIndex} eyebrow={U.faqEyebrow} title={`${cy.nameCy} ${U.faqTitleSuffix}`} testid="service-cy-faq-heading" />
        <div className="mt-10">
          <Faq items={cy.faqs} idPrefix="service-cy-faq" />
        </div>
      </section>

      <section className="border-t border-[#1C2027] bg-[#0D1015] py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <SectionHead index={U.relatedIndex} eyebrow={U.relatedEyebrow} title={U.relatedTitle} testid="service-cy-related-heading" />
          <div className="mt-8 grid gap-px border border-[#1C2027] bg-[#1C2027] sm:grid-cols-3">
            {related.map((r) => {
              const rcy = CY_SERVICES[r.slug];
              return (
                <Link key={r.slug} to={`/cy/gwasanaethau/${r.slug}`} data-testid={`related-cy-service-${r.slug}`} className="group flex h-full flex-col justify-between bg-[#10131A] p-6 transition-colors duration-300 hover:bg-[#161A22]">
                  <div>
                    <div className="flex items-start justify-between">
                      <h3 className="font-condensed text-xl font-bold uppercase tracking-wide text-white transition-colors duration-300 group-hover:text-[#F5A800]">{rcy?.nameCy || r.name}</h3>
                      <ArrowUpRight className="h-4 w-4 text-white/30 transition-all duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-[#F5A800]" />
                    </div>
                    <p className="mt-2 text-[13px] leading-relaxed text-[#9BA1AB]">{rcy?.shortCy || r.short}</p>
                  </div>
                  <span className="mt-5 text-[10px] font-bold uppercase tracking-[0.24em] text-white/40 group-hover:text-[#F5A800]">{U.relatedCta}</span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-[#F5A800] py-12" data-testid="service-cy-cta-band">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-6 px-4 sm:px-6 lg:flex-row lg:items-center">
          <h2 className="font-condensed text-3xl font-bold uppercase leading-none text-[#0A0C10] sm:text-4xl">{U.ctaTitleIn}</h2>
          <div className="flex flex-wrap gap-3">
            <a href={PHONE_HREF} data-testid="service-cy-cta-call" className="bg-[#0A0C10] px-6 py-3.5 text-sm font-bold uppercase tracking-[0.14em] text-[#F5A800] transition-colors duration-300 hover:bg-[#1A1E26]">
              {U.ctaCallBtn} {PHONE}
            </a>
            <a href={EMAIL_HREF} data-testid="service-cy-cta-email" className="border-2 border-[#0A0C10] px-6 py-3.5 text-sm font-bold uppercase tracking-[0.14em] text-[#0A0C10] transition-colors duration-300 hover:bg-[#0A0C10] hover:text-[#F5A800]">
              {U.ctaEmailBtn}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WelshServicePage;
